import {
  EApiLanguageCode,
  IAuthenticExpressionData,
  IAuthenticExpressionSourceData,
  IClozeData,
  TVocabularyBookCategory,
  TVocabularyBookLevel,
} from '@datatype/iyagigo/api-type';

export type TAdminClozeType = 'vocabulary' | 'grammar';

export type IAdminClozeData = IClozeData;

export interface IReadAdminClozesRequest {
  page: number;
  perPage: number;
  clozeType?: TAdminClozeType | null;
  languageCode?: EApiLanguageCode | null;
}

export interface IReadAdminExpressionsRequest {
  page: number;
  perPage: number;
  languageCode?: EApiLanguageCode | null;
  contentType?: string | null;
  isPublished?: boolean | null;
  isChecked?: boolean | null;
}

export interface ICreateExpressionBatchRequest {
  languageCode: EApiLanguageCode;
  createCount: number;
  authenticExpressionSourceKey: string;
}

export interface IModifyExpressionRequest {
  expressionKey: string;
  transcriptionReviewScore?: number | null;
  transcriptionReviewComment?: string | null;
  keyExpressionReviewScore?: number | null;
  keyExpressionReviewComment?: string | null;
  publishedAt?: string | null;
}

export type IAdminExpressionData = IAuthenticExpressionData;

export type IAdminSourceData = IAuthenticExpressionSourceData;

export type TAdminSourceSort = 'checkedAt' | 'releaseDate';

export type TAdminSortOrder = 'asc' | 'desc';

// ###################################
// admin vocabulary book
// ###################################
export const VOCABULARY_BOOK_LEVEL_LABEL: Record<TVocabularyBookLevel, string> = {
  JLPT5: 'N5',
  JLPT4: 'N4',
  JLPT3: 'N3',
  JLPT2: 'N2',
  JLPT1: 'N1',
  TOEIC550: 'TOEIC 550',
  TOEIC650: 'TOEIC 650',
  TOEIC750: 'TOEIC 750',
  TOEIC850: 'TOEIC 850',
  TOEIC950: 'TOEIC 950',
  EASIEST: '매우 쉬움',
  EASY: '쉬움',
  NORMAL: '보통',
  HARD: '어려움',
  HARDEST: '매우 어려움',
};

export interface IAdminVocabularyBookBundleData {
  key: string;
  order: number;
  aiVocabulary: {
    key: string;
    source: string;
    wordClass: string | null;
    sourcePronunciation: string[];
    sourceAudioLink: string[];
    meaningKo: string[];
    meaningEn: string[];
    meaningJa: string[];
  };
}

export interface IAdminVocabularyBookData {
  key: string;
  languageCode: EApiLanguageCode;
  category: TVocabularyBookCategory;
  level: TVocabularyBookLevel;
  order: number;
  name: string | null;
  description: string | null;
  itemCount: number;
  isPlan: boolean;
  publishedAt: string | null;
  createdAt: string;
  bundles: IAdminVocabularyBookBundleData[];
}

export interface IReadAdminVocabularyBooksRequest {
  page: number;
  perPage: number;
  languageCode?: EApiLanguageCode | null;
  category?: TVocabularyBookCategory | null;
  levels?: TVocabularyBookLevel[] | null;
  published?: boolean | null;
}

export interface IReadAdminSourcesRequest {
  page: number;
  perPage: number;
  languageCode?: EApiLanguageCode | null;
  sortBy?: TAdminSourceSort | null;
  sortOrder?: TAdminSortOrder | null;
}

// ###################################
// metric
// ###################################
export type TAdminClozeDifficulty = 'EASY' | 'NORMAL' | 'HARD';

export type TAdminSubscriptionPlan = 'MONTHLY_PLAN' | 'YEARLY_PLAN';
export type TAdminSourceChannel = 'SNS' | 'STORE' | 'SEARCH' | 'ARTICLE' | 'REFERRAL' | 'ETC';
export type TAdminStudyMotivation = 'HOBBY' | 'CONVERSATION' | 'STUDY' | 'WORK' | 'ETC';
export type TAdminLanguageLevel = 'BEGINNER' | 'BASIC' | 'ELEMENTARY' | 'INTERMEDIATE' | 'ADVANCED';
export type TAdminStudyTimeGoal = 'MINIMAL' | 'MEDIUM' | 'CONSIDERABLE' | 'MAXIMAL';

export interface IAdminStudentMetricData {
  studentKey: string;
  userId: number | null;
  username: string | null;
  email: string | null;
  studentCreatedAt: string | null;
  recentStudiedDates?: string[];
  /** 신규 학습 기록 지표 배포 전 응답 호환용 */
  recentAttendedDates?: string[];
  firstStudiedAt: string | null;
  lastStudiedAt: string | null;
  pushPlatform: 'IOS' | 'ANDROID' | null;
  hasPushToken: boolean;
  attendancePlatform: 'WEB' | 'IOS' | 'ANDROID' | null;
  /** 누적 앱 출석일 수. 지표 배포 전 응답은 0으로 표시한다. */
  attendanceCount?: number;
  /** 누적 OPEN 출석일. 출석 횟수 툴팁에 사용한다. */
  attendanceDates?: string[];
  /** 학습 종류별 누적 출석일 수. key는 EAttendanceType 이름이다. */
  studyAttendanceCount?: Record<string, number>;
  subscriptionPlan: TAdminSubscriptionPlan | null;
  bookmarkCount: number;
  bookmarkRepeatCount: number;
  incorrectCount: number;
  incorrectRepeatCount: number;
  englishVocabularyStudyTime: number;
  englishGrammarStudyTime: number;
  japaneseVocabularyStudyTime: number;
  japaneseGrammarStudyTime: number;
  englishCharacterStudyTime: number;
  japaneseCharacterStudyTime: number;
  englishClassroomGrammarStudyTime: number;
  japaneseClassroomGrammarStudyTime: number;
  /** 기본표현 지표 배포 전 응답 호환용 */
  japaneseBasicExpressionStudyTime?: number;
  /** 생활회화 지표 배포 전 응답 호환용 */
  japanesePracticalConversationStudyTime?: number;
  englishMiniStoryStudyTime: number;
  japaneseMiniStoryStudyTime: number;
  englishVocabularyBookStudyTime: number;
  japaneseVocabularyBookStudyTime: number;
  englishAuthenticExpressionStudyTime: number;
  japaneseAuthenticExpressionStudyTime: number;
  englishSpeakingStudyTime?: number;
  englishWorksheetStudyTime?: number;
  japaneseSpeakingStudyTime?: number;
  japaneseWorksheetStudyTime?: number;
  englishVocabularyProblemCount: number;
  englishGrammarProblemCount: number;
  japaneseVocabularyProblemCount: number;
  japaneseGrammarProblemCount: number;
  englishCharacterStudyCount: number;
  japaneseCharacterStudyCount: number;
  englishClassroomGrammarStudyCount: number;
  japaneseClassroomGrammarStudyCount: number;
  japaneseBasicExpressionStudyCount?: number;
  japanesePracticalConversationStudyCount?: number;
  englishMiniStoryStudyCount: number;
  japaneseMiniStoryStudyCount: number;
  englishAuthenticExpressionStudyCount: number;
  japaneseAuthenticExpressionStudyCount: number;
  englishSpeakingStudyCount?: number;
  englishWorksheetStudyCount?: number;
  japaneseSpeakingStudyCount?: number;
  japaneseWorksheetStudyCount?: number;
  englishVocabularyClozeLevel: TAdminClozeDifficulty | null;
  englishGrammarClozeLevel: TAdminClozeDifficulty | null;
  japaneseVocabularyClozeLevel: TAdminClozeDifficulty | null;
  japaneseGrammarClozeLevel: TAdminClozeDifficulty | null;
  englishVocabularyClozeGoal: number | null;
  englishGrammarClozeGoal: number | null;
  japaneseVocabularyClozeGoal: number | null;
  japaneseGrammarClozeGoal: number | null;
  sourceChannel: TAdminSourceChannel | null;
  englishStudyMotivation: TAdminStudyMotivation[];
  englishLanguageLevel: TAdminLanguageLevel | null;
  englishStudyTimeGoal: TAdminStudyTimeGoal | null;
  japaneseStudyMotivation: TAdminStudyMotivation[];
  japaneseLanguageLevel: TAdminLanguageLevel | null;
  japaneseStudyTimeGoal: TAdminStudyTimeGoal | null;
  didStudyEnglishExpression: boolean;
  didStudyJapaneseExpression: boolean;
  didStudyEnglishStory: boolean;
  didStudyJapaneseStory: boolean;
  englishTestVocabularyBookStudyCount: number;
  englishStudyVocabularyBookStudyCount: number;
  japaneseTestVocabularyBookStudyCount: number;
  japaneseStudyVocabularyBookStudyCount: number;
  didStudyEnglishCharacter: boolean;
  didStudyJapaneseCharacter: boolean;
  didStudyEnglishClassroomGrammar: boolean;
  didStudyJapaneseClassroomGrammar: boolean;
}

export interface IAdminDailyMetricData {
  date: string;
  signupCount: number;
  activeStudentCount: number;
  /** 재방문 지표 배포 전 응답은 0으로 표시한다. */
  returningStudentCount?: number;
  studiedStudentCount: number;
  englishStudyTime: number;
  japaneseStudyTime: number;
}

export interface IReadAdminStudentMetricsRequest {
  date: string;
  page: number;
  perPage: number;
  studied?: boolean;
}

export interface IReadAdminDailyMetricsRequest {
  page: number;
  perPage: number;
}

export interface IAdminOverviewMetricData {
  totalMemberCount: number;
  paidMemberCount: number;
  monthlyPlanMemberCount: number;
  yearlyPlanMemberCount: number;
  appStorePaidMemberCount: number;
  playStorePaidMemberCount: number;
  activeMemberCount: number;
  lirTargetCount: number;
  lirAchievedCount: number;
  averageTimeToValueMinutes: number;
  d1TargetCount: number;
  d1RetainedCount: number;
  d7TargetCount: number;
  d7RetainedCount: number;
  w1TargetCount: number;
  w1RetainedCount: number;
  w1MinStudyDays: number;
  surveyDistributions: IAdminSurveyDistributionData[];
}

export interface IAdminSurveyDistributionData {
  surveyKey:
    | 'SOURCE_CHANNEL'
    | 'EN_STUDY_MOTIVATION'
    | 'EN_LANGUAGE_LEVEL'
    | 'EN_STUDY_TIME_GOAL'
    | 'JA_STUDY_MOTIVATION'
    | 'JA_LANGUAGE_LEVEL'
    | 'JA_STUDY_TIME_GOAL';
  respondentCount: number;
  answers: { answer: string; count: number }[];
}

export interface IAdminExpressionMetricData {
  englishPublishedCount: number;
  englishScheduledCount: number;
  japanesePublishedCount: number;
  japaneseScheduledCount: number;
  englishConsumedSourceCount: number;
  englishRemainingSourceCount: number;
  japaneseConsumedSourceCount: number;
  japaneseRemainingSourceCount: number;
}

export interface IAdminClozeMetricData {
  englishTotalCount: number;
  englishVocabularyEasyCount: number;
  englishVocabularyNormalCount: number;
  englishVocabularyHardCount: number;
  englishGrammarEasyCount: number;
  englishGrammarNormalCount: number;
  englishGrammarHardCount: number;
  englishUsedCount: number;
  englishUnusedCount: number;
  japaneseTotalCount: number;
  japaneseVocabularyEasyCount: number;
  japaneseVocabularyNormalCount: number;
  japaneseVocabularyHardCount: number;
  japaneseGrammarEasyCount: number;
  japaneseGrammarNormalCount: number;
  japaneseGrammarHardCount: number;
  japaneseUsedCount: number;
  japaneseUnusedCount: number;
}
